import type { ExerciseTemplate } from '@yuan-math/exercises';
import { add as addFraction, fraction, sub as subFraction, mul as mulFraction, type Fraction } from '@yuan-math/math';
import {
  choiceExercise,
  expressionExercise,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  signedConstant,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * Techniques of integration.
 *
 * Two constraints shape almost every item in this file, and both are worth
 * stating because they are not obvious from the templates themselves.
 *
 * **The grader has no transcendental functions.** `areExpressionsEquivalent`
 * parses sums, products and powers of single letters and nothing else — there is
 * no sin, no ln, no e. So an answer of −cos³x/3 + cos⁵x/5 cannot be typed into a
 * box and cannot be graded, and any item whose answer contains one is asked as a
 * *value* (a definite integral, a coefficient, a constant) or as a *choice*
 * (which substitution, which decomposition, which transformed integral). That is
 * a restriction on the questions and not on the mathematics: the definite
 * integral of sin³x cos²x is a rational number, and asking for it tests the same
 * peel-and-substitute as asking for the antiderivative.
 *
 * **A technique's misconception is usually invisible in the answer's shape.**
 * A dropped ½ from du, a minus sign lost in the parts formula, limits left
 * belonging to x — each produces a number that looks exactly like a right one.
 * So the traps here are computed from the *wrong procedure carried out
 * correctly*, rather than from a plausible-looking wrong number, and several
 * templates are pinned to parameter ranges that keep the wrong procedure
 * distinguishable from the right one.
 *
 * The pins that are decisions rather than accidents:
 *
 * `parts-exponential-definite` only attaches its "v not integrated" trap when
 * a ≥ 2. At a = 1 the antiderivative of e^{ax} *is* e^{ax}, the wrong procedure
 * and the right one agree exactly, and the trap would be sitting on the answer.
 *
 * `trig-sub-arctan` drops its second trap at a = 2, where π/(2a) and π/4
 * coincide — two traps normalising to the same number would hand one wrong
 * answer two different diagnoses.
 *
 * `partial-fractions-cover-up` refuses |A| = |B|, which buys two things at once.
 * It keeps the "other constant" trap from colliding with the "sign reversed"
 * one — B = −A would make them the same number — and it rules out A + B = 0,
 * which would print the numerator as a bare constant and pose a different, much
 * easier question than the one intended.
 */

/** A number for prose rather than for LaTeX — a real minus sign, not a hyphen. */
const minus = (value: number | string): string => String(value).replace(/-/g, '−');

/** `3x`, `x`, `-x` — a coefficient in front of something, with 1 dropped. */
const coeff = (value: number, body: string): string =>
  value === 1 ? body : value === -1 ? `-${body}` : `${value}${body}`;

/** `(x - 3)` or `(x + 3)`, never `(x )`. Callers keep the root non-zero. */
const linearFactor = (root: number): string => `(x ${signedConstant(-root)})`;

/** ∫₀^{π/2} sinᵐx cosⁿx dx for odd m, as an exact rational. */
function oddSineIntegral(m: number, n: number): Fraction {
  // Substituting u = cos x turns it into ∫₀¹ (1 − u²)^{(m−1)/2} uⁿ du, and the
  // binomial expansion of that bracket is a short alternating sum.
  const half = (m - 1) / 2;
  let total = fraction(0, 1);
  let binomial = 1;
  for (let k = 0; k <= half; k += 1) {
    const term = fraction(binomial, n + 2 * k + 1);
    total = k % 2 === 0 ? addFraction(total, term) : subFraction(total, term);
    binomial = (binomial * (half - k)) / (k + 1);
  }
  return total;
}

export const INTEGRATION_TECHNIQUE_TEMPLATES: ExerciseTemplate[] = [
  /* -------------------------------------------------------- substitution */
  template({
    id: 'substitution-power-definite',
    conceptId: 'u-substitution',
    description: 'Evaluate a definite integral by substituting the inside of a bracket.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const b = rng.int(1, 2);
      const c = rng.int(1, 4);
      const n = rng.int(2, 3);
      const top = b * b + c;
      const answer = ((top ** (n + 1) - c ** (n + 1)) / (2 * (n + 1)));
      const constantNotBorrowed = 2 * answer;
      const limitsNotChanged = b ** (n + 1) / (2 * (n + 1));

      return numericExercise({
        prompt: 'Evaluate the integral. Give your answer to two decimal places.',
        latex: `\\int_{0}^{${b}} x\\,(x^{2} + ${c})^{${n}}\\,dx`,
        value: answer,
        tolerance: within(0.01, answer),
        summary: `With u = x² + ${c}, du = 2x dx, so x dx is half of du — and the limits become u = ${c} and u = ${top}. The integral is ½∫u^${n} du = (${top}^${n + 1} − ${c}^${n + 1})/${2 * (n + 1)} = ${answer.toFixed(2)}.`,
        hint: 'Let u be the bracket. Then work out what x dx is in terms of du, and convert the limits.',
        steps: [
          { label: 'Name the inside.', latex: `u = x^{2} + ${c}, \\qquad du = 2x\\,dx` },
          { label: 'Borrow the constant: the integral offers x dx, not 2x dx.', latex: 'x\\,dx = \\tfrac{1}{2}\\,du' },
          { label: 'Convert the limits — they are x-values.', latex: `x = 0 \\Rightarrow u = ${c}, \\qquad x = ${b} \\Rightarrow u = ${top}` },
          { label: 'Rewrite and integrate.', latex: `\\tfrac{1}{2}\\int_{${c}}^{${top}} u^{${n}}\\,du = \\tfrac{1}{2}\\left[\\frac{u^{${n + 1}}}{${n + 1}}\\right]_{${c}}^{${top}}` },
          { label: 'Evaluate.', latex: `${answer.toFixed(2)}` },
        ],
        traps: [
          trap(constantNotBorrowed.toFixed(2), 'substitution-constant-not-adjusted'),
          trap(limitsNotChanged.toFixed(2), 'substitution-limits-not-changed'),
        ],
      });
    },
  }),

  template({
    id: 'substitution-transformed-integral',
    conceptId: 'u-substitution',
    description: 'Choose the integral a substitution actually produces.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const b = rng.int(1, 4);
      const c = rng.int(1, 6);
      const n = rng.int(2, 5);
      const top = b * b + c;

      return choiceExercise({
        prompt: `Substituting u = x² + ${c}, which integral is this one equal to?`,
        latex: `\\int_{0}^{${b}} x\\,(x^{2} + ${c})^{${n}}\\,dx`,
        ...options(
          [
            { latex: `\\frac{1}{2}\\int_{${c}}^{${top}} u^{${n}}\\,du`, correct: true },
            { latex: `\\int_{${c}}^{${top}} u^{${n}}\\,du`, misconceptionId: 'substitution-constant-not-adjusted' },
            { latex: `\\frac{1}{2}\\int_{0}^{${b}} u^{${n}}\\,du`, misconceptionId: 'substitution-limits-not-changed' },
            { latex: `\\frac{1}{2}\\int_{${c}}^{${top}} u^{${n}}\\,dx`, misconceptionId: 'substitution-dx-not-converted' },
          ],
          rng.shuffle,
        ),
        summary: `du = 2x dx, so x dx is ½ du — that is where the half comes from. The limits are x-values and must be converted: x = 0 gives u = ${c}, and x = ${b} gives u = ${top}. And the differential is du, not dx: nothing in x may survive.`,
        hint: 'Three things change: the integrand, the differential, and the limits.',
      });
    },
  }),

  template({
    id: 'substitution-linear-power',
    conceptId: 'u-substitution',
    description: 'Antidifferentiate a bracket raised to a power.',
    difficulties: [2, 3, 4],
    build: ({ rng }) => {
      // a ≥ 2 keeps the inside derivative from being 1, where the "constant not
      // borrowed" trap would grade as the correct answer.
      const a = rng.int(2, 5);
      const b = rng.int(1, 6);
      const n = rng.int(2, 4);
      const inner = `(${a}*x + ${b})`;
      const answer = `${inner}^${n + 1}/${a * (n + 1)}`;
      const constantNotBorrowed = `${inner}^${n + 1}/${n + 1}`;
      const differentiated = `${a * n}*${inner}^${n - 1}`;

      return expressionExercise({
        prompt: 'Find the antiderivative. Leave out the constant of integration.',
        latex: `\\int (${a}x + ${b})^{${n}}\\,dx`,
        canonical: answer,
        summary: `With u = ${a}x + ${b}, du = ${a} dx, so dx = du/${a}. The integral is (1/${a})·u^${n + 1}/${n + 1}, which is (${a}x + ${b})^${n + 1}/${a * (n + 1)}.`,
        hint: `The inside differentiates to ${a}, so a factor of 1/${a} has to come out.`,
        steps: [
          { label: 'Name the inside and differentiate it.', latex: `u = ${a}x + ${b}, \\qquad du = ${a}\\,dx` },
          { label: 'So dx has to be converted.', latex: `dx = \\frac{du}{${a}}` },
          { label: 'Integrate in u.', latex: `\\frac{1}{${a}}\\cdot\\frac{u^{${n + 1}}}{${n + 1}}` },
          { label: 'Put the bracket back.', latex: `\\frac{(${a}x + ${b})^{${n + 1}}}{${a * (n + 1)}}` },
        ],
        traps: [
          trap(constantNotBorrowed, 'substitution-constant-not-adjusted'),
          trap(differentiated, 'antiderivative-differentiated-instead'),
        ],
      });
    },
  }),

  /* ----------------------------------------------------- integration by parts */
  template({
    id: 'parts-exponential-definite',
    conceptId: 'integration-by-parts',
    description: 'A definite integral of x times an exponential, by parts.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 2);
      const b = rng.int(1, 3);
      const c = rng.int(1, 6);
      const growth = Math.exp(a * b);
      const uvTerm = (b * growth) / a;
      const tradedIntegral = (growth - 1) / (a * a);
      const answer = c * (uvTerm - tradedIntegral);
      const signDropped = c * (uvTerm + tradedIntegral);
      // With a = 1 the antiderivative of e^{ax} is e^{ax} itself, so failing to
      // integrate dv produces the correct answer and the trap must be dropped.
      const vNotIntegrated = c * (b * growth - (growth - 1) / a);

      return numericExercise({
        prompt: 'Evaluate the integral. Give your answer to two decimal places.',
        latex: `\\int_{0}^{${b}} ${coeff(c, 'x')}e^{${coeff(a, 'x')}}\\,dx`,
        value: answer,
        tolerance: within(0.01, answer),
        summary: `Take u = x and dv = e^{${coeff(a, 'x')}} dx, so du = dx and v = e^{${coeff(a, 'x')}}/${a}. Then uv evaluates to ${uvTerm.toFixed(3)} and the traded integral to ${tradedIntegral.toFixed(3)}, giving ${c} × (${uvTerm.toFixed(3)} − ${tradedIntegral.toFixed(3)}) = ${answer.toFixed(2)}.`,
        hint: 'Algebraic beats exponential, so u is the x. Remember that v is the integral of dv.',
        steps: [
          { label: 'Choose u and dv by LIATE.', latex: `u = x, \\qquad dv = e^{${coeff(a, 'x')}}\\,dx` },
          { label: 'Differentiate u; integrate dv.', latex: `du = dx, \\qquad v = \\frac{e^{${coeff(a, 'x')}}}{${a}}` },
          { label: 'Apply the formula, with the constant outside.', latex: `${c}\\left(\\left[\\frac{x e^{${coeff(a, 'x')}}}{${a}}\\right]_{0}^{${b}} - \\frac{1}{${a}}\\int_{0}^{${b}} e^{${coeff(a, 'x')}}\\,dx\\right)` },
          { label: 'Evaluate both pieces.', latex: `${c}\\big(${uvTerm.toFixed(3)} - ${tradedIntegral.toFixed(3)}\\big)` },
          { label: 'Arithmetic.', latex: `${answer.toFixed(2)}` },
        ],
        traps: [
          trap(signDropped.toFixed(2), 'parts-sign-dropped'),
          ...(a === 1 ? [] : [trap(vNotIntegrated.toFixed(2), 'parts-v-not-integrated')]),
        ],
      });
    },
  }),

  template({
    id: 'parts-logarithm-definite',
    conceptId: 'integration-by-parts',
    description: 'A definite integral of a power times a logarithm, by parts.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const m = rng.int(1, 3);
      const b = rng.int(2, 6);
      const power = b ** (m + 1);
      const uvTerm = (power * Math.log(b)) / (m + 1);
      const tradedIntegral = (power - 1) / (m + 1) ** 2;
      const answer = uvTerm - tradedIntegral;
      const signDropped = uvTerm + tradedIntegral;
      const vNotIntegrated = b ** m * Math.log(b) - (b ** m - 1) / m;

      return numericExercise({
        prompt: 'Evaluate the integral. Give your answer to two decimal places.',
        latex: `\\int_{1}^{${b}} ${m === 1 ? 'x' : `x^{${m}}`}\\ln x\\,dx`,
        value: answer,
        tolerance: within(0.01, answer),
        summary: `Logarithmic beats algebraic, so u = ln x and dv = x^${m} dx. That gives du = dx/x and v = x^${m + 1}/${m + 1}, and the traded integral collapses to ∫x^${m} dx/${m + 1}. The value is ${answer.toFixed(2)}.`,
        hint: 'L comes before A in LIATE. Take u to be the logarithm, however small it looks.',
        steps: [
          { label: 'Choose u and dv.', latex: `u = \\ln x, \\qquad dv = ${m === 1 ? 'x' : `x^{${m}}`}\\,dx` },
          { label: 'Differentiate and integrate.', latex: `du = \\frac{dx}{x}, \\qquad v = \\frac{x^{${m + 1}}}{${m + 1}}` },
          { label: 'Apply the formula.', latex: `\\left[\\frac{x^{${m + 1}}\\ln x}{${m + 1}}\\right]_{1}^{${b}} - \\frac{1}{${m + 1}}\\int_{1}^{${b}} x^{${m}}\\,dx` },
          { label: 'The x cancels in the traded integral, which is why this choice works.', latex: `= ${uvTerm.toFixed(3)} - ${tradedIntegral.toFixed(3)}` },
          { label: 'Evaluate.', latex: `${answer.toFixed(2)}` },
        ],
        traps: [
          trap(signDropped.toFixed(2), 'parts-sign-dropped'),
          trap(vNotIntegrated.toFixed(2), 'parts-v-not-integrated'),
        ],
      });
    },
  }),

  template({
    id: 'parts-after-one-application',
    conceptId: 'integration-by-parts',
    description: 'Identify the result of one application of the parts formula.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(2, 9);

      return choiceExercise({
        prompt: `Applying integration by parts with u = x and dv = e^{${a}x} dx, which of these equals the integral?`,
        latex: `\\int x\\,e^{${a}x}\\,dx`,
        ...options(
          [
            { latex: `\\frac{x e^{${a}x}}{${a}} - \\frac{1}{${a}}\\int e^{${a}x}\\,dx`, correct: true },
            { latex: `\\frac{x e^{${a}x}}{${a}} + \\frac{1}{${a}}\\int e^{${a}x}\\,dx`, misconceptionId: 'parts-sign-dropped' },
            { latex: `x e^{${a}x} - \\int e^{${a}x}\\,dx`, misconceptionId: 'parts-v-not-integrated' },
            { latex: `\\frac{x^{2}}{2}e^{${a}x} - \\frac{${a}}{2}\\int x^{2} e^{${a}x}\\,dx`, misconceptionId: 'parts-choice-inverted' },
          ],
          rng.shuffle,
        ),
        summary: `v is the integral of dv, so v = e^{${a}x}/${a} — not e^{${a}x}. The formula is uv minus ∫v du, and du = dx. The last option is what happens if u and dv are swapped: the new integral has an x² in it and is worse than the one you started with.`,
        hint: 'Fill in all four boxes — u, dv, du and v — before writing anything down.',
      });
    },
  }),

  /* ------------------------------------------------- trigonometric integrals */
  template({
    id: 'trig-odd-power-definite',
    conceptId: 'trigonometric-integrals',
    description: 'Evaluate a sine–cosine integral with an odd power, over a quarter period.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const m = rng.pick([3, 5]);
      const n = rng.int(1, 5);
      const k = rng.int(1, 3);
      const base = oddSineIntegral(m, n);
      const answer = mulFraction(fraction(k, 1), base);
      const conversionSkipped = mulFraction(fraction(k, 1), fraction(1, n + 1));

      return fractionExercise({
        prompt: 'Evaluate the integral. Give your answer as a fraction.',
        latex: `\\int_{0}^{\\pi/2} ${coeff(k, '')}\\sin^{${m}}x \\cos^{${n}}x\\,dx`,
        value: answer,
        summary: `The sine power is odd, so peel one sine off and convert the rest with sin²x = 1 − cos²x. With u = cos x the integral becomes ${k}∫₀¹ (1 − u²)^${(m - 1) / 2} u^${n} du, which evaluates to ${answer.n}/${answer.d}.`,
        hint: 'One of the powers is odd. Peel a single factor of it off to be du, and convert what is left.',
        steps: [
          { label: 'Peel one sine off; the rest is an even power.', latex: `\\int_{0}^{\\pi/2} \\sin^{${m - 1}}x \\cos^{${n}}x \\cdot \\sin x\\,dx` },
          { label: 'Convert with the Pythagorean identity.', latex: `\\sin^{${m - 1}}x = (1 - \\cos^{2}x)^{${(m - 1) / 2}}` },
          { label: 'Substitute; the minus from du flips the limits back.', latex: `u = \\cos x, \\qquad du = -\\sin x\\,dx` },
          { label: 'Integrate the polynomial in u.', latex: `${k}\\int_{0}^{1} (1 - u^{2})^{${(m - 1) / 2}} u^{${n}}\\,du` },
          { label: 'Evaluate.', latex: `\\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [
          fractionTrap(conversionSkipped, 'trig-parity-misread'),
          fractionTrap(fraction(0, 1), 'trig-power-rule-misapplied'),
        ],
      });
    },
  }),

  template({
    id: 'trig-even-power-definite',
    conceptId: 'trigonometric-integrals',
    description: 'Evaluate an even power of sine or cosine over a quarter period.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const k = rng.int(1, 12);
      const isCosine = rng.bool();
      const name = isCosine ? '\\cos' : '\\sin';
      const answer = (k * Math.PI) / 4;
      const halfForgotten = (k * Math.PI) / 2;
      const powerRule = isCosine ? -k / 3 : k / 3;

      return numericExercise({
        prompt: 'Evaluate the integral. Give your answer to three decimal places.',
        latex: `\\int_{0}^{\\pi/2} ${coeff(k, '')}${name}^{2}x\\,dx`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `Both powers are even, so use the half-angle identity: ${isCosine ? 'cos²x = (1 + cos 2x)/2' : 'sin²x = (1 − cos 2x)/2'}. The cos 2x term integrates to sin 2x/2, which vanishes at both ends, leaving ${k} × ½ × π/2 = ${answer.toFixed(3)}.`,
        hint: 'There is no factor to peel off. Lower the power with a half-angle identity instead.',
        steps: [
          { label: 'Apply the half-angle identity.', latex: `\\int_{0}^{\\pi/2} ${k === 1 ? '' : `${k}`}\\cdot\\frac{1 ${isCosine ? '+' : '-'} \\cos 2x}{2}\\,dx` },
          { label: 'Split into a constant and an oscillation.', latex: `\\frac{${k}}{2}\\int_{0}^{\\pi/2} dx ${isCosine ? '+' : '-'} \\frac{${k}}{2}\\int_{0}^{\\pi/2}\\cos 2x\\,dx` },
          { label: 'The second piece vanishes: sin π and sin 0 are both zero.', latex: `\\frac{${k}}{2}\\cdot\\frac{\\pi}{2}` },
          { label: 'Evaluate.', latex: `${answer.toFixed(3)}` },
        ],
        traps: [
          trap(halfForgotten.toFixed(3), 'trig-parity-misread'),
          trap(powerRule.toFixed(3), 'trig-power-rule-misapplied'),
        ],
      });
    },
  }),

  template({
    id: 'trig-method-choice',
    conceptId: 'trigonometric-integrals',
    description: 'Choose the method that fits the parities of the two powers.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      // Both powers odd is excluded: either peel works, so there is no single
      // right answer and three of the four options would be defensible.
      const pairs: [number, number][] = [];
      for (let m = 1; m <= 4; m += 1) {
        for (let n = 1; n <= 4; n += 1) {
          if (m % 2 === 1 && n % 2 === 1) continue;
          pairs.push([m, n]);
        }
      }
      const [m, n] = rng.pick(pairs);
      const sineOdd = m % 2 === 1;
      const cosineOdd = n % 2 === 1;
      const bothEven = !sineOdd && !cosineOdd;

      const summary = bothEven
        ? 'Both powers are even, so there is nothing to peel: peeling from an even power leaves an odd one, which does not convert. The half-angle identities lower both powers instead.'
        : sineOdd
          ? 'The sine power is odd. Peel one sine off to be du, convert the remaining even power with sin²x = 1 − cos²x, and substitute u = cos x.'
          : 'The cosine power is odd. Peel one cosine off to be du, convert the remaining even power with cos²x = 1 − sin²x, and substitute u = sin x.';

      return choiceExercise({
        prompt: 'Which method fits this integral?',
        latex: `\\int \\sin^{${m}}x \\cos^{${n}}x\\,dx`,
        ...options(
          [
            {
              label: 'Peel off one sine and substitute u = cos x',
              correct: sineOdd,
              ...(sineOdd ? {} : { misconceptionId: 'trig-parity-misread' }),
            },
            {
              label: 'Peel off one cosine and substitute u = sin x',
              correct: cosineOdd,
              ...(cosineOdd ? {} : { misconceptionId: 'trig-parity-misread' }),
            },
            {
              label: 'Lower both powers with the half-angle identities',
              correct: bothEven,
              ...(bothEven ? {} : { misconceptionId: 'trig-parity-misread' }),
            },
            { label: 'Apply the power rule to each factor separately', misconceptionId: 'trig-power-rule-misapplied' },
          ],
          rng.shuffle,
        ),
        summary,
        hint: 'Look only at whether each power is odd or even. That decides it before any work happens.',
      });
    },
  }),

  /* ---------------------------------------------- trigonometric substitution */
  template({
    id: 'trig-sub-choose',
    conceptId: 'trigonometric-substitution',
    description: 'Match a quadratic root to the substitution that removes it.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const form = rng.pick(['minus', 'plus', 'secant'] as const);
      const root =
        form === 'minus' ? `\\sqrt{${a * a} - x^{2}}` : form === 'plus' ? `\\sqrt{${a * a} + x^{2}}` : `\\sqrt{x^{2} - ${a * a}}`;
      const answers = {
        minus: `x = ${a}\\sin\\theta`,
        plus: `x = ${a}\\tan\\theta`,
        secant: `x = ${a}\\sec\\theta`,
      };
      const explanation = {
        minus: `a² − x² needs 1 − sin²θ = cos²θ, so x = ${a} sin θ and the root becomes ${a} cos θ.`,
        plus: `a² + x² needs 1 + tan²θ = sec²θ, so x = ${a} tan θ and the root becomes ${a} sec θ.`,
        secant: `x² − a² needs sec²θ − 1 = tan²θ, so x = ${a} sec θ and the root becomes ${a} tan θ.`,
      };

      return choiceExercise({
        prompt: 'Which substitution removes this square root?',
        latex: root,
        ...options(
          [
            { latex: answers.minus, correct: form === 'minus', ...(form === 'minus' ? {} : { misconceptionId: 'trig-sub-form-mismatched' }) },
            { latex: answers.plus, correct: form === 'plus', ...(form === 'plus' ? {} : { misconceptionId: 'trig-sub-form-mismatched' }) },
            { latex: answers.secant, correct: form === 'secant', ...(form === 'secant' ? {} : { misconceptionId: 'trig-sub-form-mismatched' }) },
            { latex: `u = ${form === 'secant' ? `x^{2} - ${a * a}` : `${a * a} ${form === 'minus' ? '-' : '+'} x^{2}`}`, misconceptionId: 'substitution-dx-not-converted' },
          ],
          rng.shuffle,
        ),
        summary: `${explanation[form]} An ordinary substitution on the inside fails here, because its derivative — a multiple of x — is nowhere in the integrand.`,
        hint: 'Match the signs and the order of the two terms to a Pythagorean identity.',
      });
    },
  }),

  template({
    id: 'trig-sub-triangle',
    conceptId: 'trigonometric-substitution',
    description: 'Convert a trigonometric function of θ back into x using the triangle.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const wantSine = rng.bool();
      const hypotenuse = `\\sqrt{x^{2} + ${a * a}}`;
      const correct = wantSine ? `\\frac{x}{${hypotenuse}}` : `\\frac{${a}}{${hypotenuse}}`;
      const swapped = wantSine ? `\\frac{${a}}{${hypotenuse}}` : `\\frac{x}{${hypotenuse}}`;

      return choiceExercise({
        prompt: `After substituting x = ${a} tan θ, what is ${wantSine ? 'sin θ' : 'cos θ'} in terms of x?`,
        ...options(
          [
            { latex: correct, correct: true },
            { latex: swapped, misconceptionId: 'trig-sub-form-mismatched' },
            { latex: `\\frac{x}{${a}}`, misconceptionId: 'trig-sub-not-converted-back' },
            { latex: `\\frac{${hypotenuse}}{x}`, misconceptionId: 'trig-sub-form-mismatched' },
          ],
          rng.shuffle,
        ),
        summary: `x = ${a} tan θ means tan θ = x/${a}, so draw a right triangle with opposite x and adjacent ${a}. Pythagoras gives a hypotenuse of √(x² + ${a * a}), and ${wantSine ? 'sine is opposite over hypotenuse' : 'cosine is adjacent over hypotenuse'}.`,
        hint: 'Draw the triangle the substitution describes, then read the ratio off its sides.',
      });
    },
  }),

  template({
    id: 'trig-sub-arctan',
    conceptId: 'trigonometric-substitution',
    description: 'Evaluate a definite integral that needs a tangent substitution.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(2, 12);
      const answer = Math.PI / (4 * a);
      const constantNotBorrowed = Math.PI / 4;
      const wrongAngle = Math.PI / (2 * a);

      return numericExercise({
        prompt: 'Evaluate the integral. Give your answer to three decimal places.',
        latex: `\\int_{0}^{${a}} \\frac{dx}{x^{2} + ${a * a}}`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `Put x = ${a} tan θ, so dx = ${a} sec²θ dθ and the denominator becomes ${a * a} sec²θ. Everything cancels down to (1/${a})∫dθ, and the limits run from θ = 0 to θ = π/4. The value is π/(4 × ${a}) = ${answer.toFixed(3)}.`,
        hint: 'The denominator is a² + x², so x is a tangent. Convert the limits to θ and you never need the triangle.',
        steps: [
          { label: 'Substitute.', latex: `x = ${a}\\tan\\theta, \\qquad dx = ${a}\\sec^{2}\\theta\\,d\\theta` },
          { label: 'The denominator becomes a secant squared.', latex: `x^{2} + ${a * a} = ${a * a}\\sec^{2}\\theta` },
          { label: 'Cancel; one factor of 1/a survives.', latex: `\\frac{1}{${a}}\\int d\\theta` },
          { label: `Convert the limits: x = ${a} means tan θ = 1.`, latex: `\\theta: 0 \\to \\frac{\\pi}{4}` },
          { label: 'Evaluate.', latex: `\\frac{\\pi}{${4 * a}} = ${answer.toFixed(3)}` },
        ],
        traps: [
          trap(constantNotBorrowed.toFixed(3), 'substitution-constant-not-adjusted'),
          // At a = 2 the wrong angle and the missing constant give the same
          // number, and one wrong answer must never carry two diagnoses.
          ...(a === 2 ? [] : [trap(wrongAngle.toFixed(3), 'trig-sub-form-mismatched')]),
        ],
      });
    },
  }),

  /* ---------------------------------------------------------- partial fractions */
  template({
    id: 'partial-fractions-cover-up',
    conceptId: 'partial-fractions',
    description: 'Find one constant of a decomposition with two distinct linear factors.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const roots = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];
      const values = [-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6];
      const r = rng.pick(roots);
      let s = rng.pick(roots);
      if (s === r) s = r === 5 ? 4 : r + 1;
      const A = rng.pick(values);
      let B = rng.pick(values);
      // |A| = |B| makes the "other constant" trap collide with the sign trap;
      // A + B = 0 makes the numerator print as a bare constant.
      if (Math.abs(B) === Math.abs(A)) B = Math.abs(A) === 6 ? 2 : Math.abs(A) + 1;
      // Non-zero by the guard above: p = 0 needs B = −A, and |B| = |A| is refused.
      const p = A + B;
      const q = -(A * s + B * r);
      const numerator = `${coeff(p, 'x')} ${signedConstant(q)}`.trim();

      return numericExercise({
        prompt: `This rational function splits as A/(x ${signedConstant(-r)}) + B/(x ${signedConstant(-s)}). What is A?`,
        latex: `\\frac{${numerator}}{${linearFactor(r)}${linearFactor(s)}}`,
        value: A,
        summary: `Clear the denominators: ${minus(numerator)} = A(x ${minus(signedConstant(-s))}) + B(x ${minus(signedConstant(-r))}). Putting x = ${minus(r)} kills the B term and leaves A = ${minus(A)}.`,
        hint: `Substitute the root of the factor sitting under A — that is x = ${minus(r)} — and the other constant disappears.`,
        steps: [
          { label: 'Multiply through by the whole denominator.', latex: `${numerator} = A${linearFactor(s)} + B${linearFactor(r)}` },
          { label: `Substitute x = ${r}, which kills the B term.`, latex: `${p * r + q} = A(${r - s})` },
          { label: 'Divide.', latex: `A = ${A}` },
        ],
        traps: [
          trap(B, 'partial-fractions-constant-swapped'),
          trap(-A, 'partial-fractions-cover-up-sign'),
        ],
      });
    },
  }),

  template({
    id: 'partial-fractions-form',
    conceptId: 'partial-fractions',
    description: 'Choose the shape of the decomposition for a given denominator.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const r = rng.int(1, 6);
      const s = rng.int(1, 6) + 6;
      const c = rng.int(1, 9);
      const kind = rng.pick(['distinct', 'repeated', 'quadratic'] as const);
      const denominator = {
        distinct: `${linearFactor(r)}${linearFactor(s)}`,
        repeated: `${linearFactor(r)}^{2}`,
        quadratic: `${linearFactor(r)}(x^{2} + ${c})`,
      }[kind];
      const shapes = {
        distinct: `\\frac{A}{x ${signedConstant(-r)}} + \\frac{B}{x ${signedConstant(-s)}}`,
        repeated: `\\frac{A}{x ${signedConstant(-r)}} + \\frac{B}{(x ${signedConstant(-r)})^{2}}`,
        quadratic: `\\frac{A}{x ${signedConstant(-r)}} + \\frac{Bx + C}{x^{2} + ${c}}`,
      };
      const explanation = {
        distinct: 'Two distinct linear factors give one constant each.',
        repeated: `A factor to the power 2 contributes two terms — one over (x ${minus(signedConstant(-r))}) and one over its square. With only the squared term there are not enough constants to match a general numerator.`,
        quadratic: 'An irreducible quadratic takes a linear numerator, Bx + C, because the numerator is always one degree below the factor beneath it.',
      };

      return choiceExercise({
        prompt: 'Which decomposition has the right shape for this denominator?',
        latex: `\\frac{5x + 2}{${denominator}}`,
        ...options(
          [
            { latex: shapes.distinct, correct: kind === 'distinct', ...(kind === 'distinct' ? {} : { misconceptionId: 'partial-fractions-repeated-term-missing' }) },
            { latex: shapes.repeated, correct: kind === 'repeated', ...(kind === 'repeated' ? {} : { misconceptionId: 'partial-fractions-repeated-term-missing' }) },
            { latex: shapes.quadratic, correct: kind === 'quadratic', ...(kind === 'quadratic' ? {} : { misconceptionId: 'partial-fractions-quadratic-given-constant' }) },
            { latex: `\\frac{A}{${denominator}}`, misconceptionId: 'partial-fractions-improper-not-divided' },
          ],
          rng.shuffle,
        ),
        summary: explanation[kind],
        hint: 'Every factor of the denominator contributes its own term, and a repeated factor contributes one for every power.',
      });
    },
  }),

  template({
    id: 'partial-fractions-repeated',
    conceptId: 'partial-fractions',
    description: 'Find the constant over the squared factor of a repeated decomposition.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const r = rng.pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
      const p = rng.pick([-6, -5, -4, -3, -2, 2, 3, 4, 5, 6]);
      /*
       * B = pr + q is the answer, and q and p are the two traps. All three have
       * to differ: B = q would put a trap on the answer, and q = p would give
       * one wrong number two diagnoses. Walking q rather than rejecting the
       * whole parameter set keeps the generator total.
       */
      let q = rng.int(-9, 9);
      let B = p * r + q;
      while (B === q || B === p || q === p) {
        q += 1;
        B = p * r + q;
      }
      const numerator = `${coeff(p, 'x')} ${signedConstant(q)}`.trim();

      return numericExercise({
        prompt: `This splits as A/(x ${signedConstant(-r)}) + B/(x ${signedConstant(-r)})². What is B?`,
        latex: `\\frac{${numerator}}{${linearFactor(r)}^{2}}`,
        value: B,
        summary: `Clearing the denominators gives ${minus(numerator)} = A(x ${minus(signedConstant(-r))}) + B. Putting x = ${minus(r)} kills the A term, leaving B = ${minus(p)}(${minus(r)}) ${minus(signedConstant(q))} = ${minus(B)}.`,
        hint: `Substitute the repeated root, x = ${minus(r)}. Only the constant over the squared factor survives.`,
        steps: [
          { label: 'Multiply through by the squared factor.', latex: `${numerator} = A${linearFactor(r)} + B` },
          { label: `Substitute x = ${r}.`, latex: `${p}(${r}) ${signedConstant(q)} = B` },
          { label: 'Evaluate.', latex: `B = ${B}` },
        ],
        traps: [
          trap(q, 'partial-fractions-repeated-term-missing'),
          trap(p, 'partial-fractions-constant-swapped'),
        ],
      });
    },
  }),

  /* --------------------------------------------------------- improper integrals */
  template({
    id: 'improper-p-value',
    conceptId: 'improper-integrals',
    description: 'Evaluate a convergent improper integral over an infinite interval.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const c = rng.int(1, 9);
      const p = rng.int(2, 6);
      const answer = c / (p - 1);
      const backwards = -answer;

      return numericExercise({
        prompt: 'Evaluate the improper integral. Give your answer to three decimal places.',
        latex: `\\int_{1}^{\\infty} \\frac{${c}}{x^{${p}}}\\,dx`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `Replace ∞ by t and take the limit. The antiderivative is ${c}x^{${1 - p}}/${1 - p}, which tends to 0 as t grows, so the value is ${c}/${p - 1} = ${answer.toFixed(3)}.`,
        hint: `This is a p-integral with p = ${p}. It converges because p > 1, and the value is c/(p − 1).`,
        steps: [
          { label: 'Cut the interval short.', latex: `\\lim_{t\\to\\infty}\\int_{1}^{t} ${c}x^{-${p}}\\,dx` },
          { label: 'Antidifferentiate.', latex: `\\lim_{t\\to\\infty}\\left[\\frac{${c}x^{${1 - p}}}{${1 - p}}\\right]_{1}^{t}` },
          { label: 'The upper term vanishes because p > 1.', latex: `0 - \\frac{${c}}{${1 - p}}` },
          { label: 'Evaluate.', latex: `${answer.toFixed(3)}` },
        ],
        traps: [trap(backwards.toFixed(3), 'ftc-limits-subtracted-backwards')],
      });
    },
  }),

  template({
    id: 'improper-which-converges',
    conceptId: 'improper-integrals',
    description: 'Pick the convergent one from four improper integrals.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(2, 5);
      const q = rng.int(2, 5);
      const atInfinity = rng.bool();

      const correct = atInfinity
        ? `\\int_{1}^{\\infty} \\frac{dx}{x^{${p}}}`
        : `\\int_{0}^{1} \\frac{dx}{x^{1/${q}}}`;
      const wrongEnd = atInfinity
        ? `\\int_{0}^{1} \\frac{dx}{x^{${p}}}`
        : `\\int_{1}^{\\infty} \\frac{dx}{x^{1/${q}}}`;

      return choiceExercise({
        prompt: 'Which of these improper integrals converges?',
        ...options(
          [
            { latex: correct, correct: true },
            { latex: wrongEnd, misconceptionId: 'improper-p-test-reversed' },
            { latex: '\\int_{1}^{\\infty} \\frac{dx}{x}', misconceptionId: 'improper-p-test-reversed' },
            { latex: `\\int_{-1}^{1} \\frac{dx}{x^{${2 * q - 1}}}`, misconceptionId: 'improper-interior-discontinuity-ignored' },
          ],
          rng.shuffle,
        ),
        summary: atInfinity
          ? `Out at infinity, ∫x^(−p) converges when p > 1 — so p = ${p} converges and p = 1 does not. Near zero the test points the other way, which is why the same exponent fails there.`
          : `Near zero, ∫x^(−p) converges when p < 1 — so an exponent of 1/${q} converges there and diverges at infinity. The last option has its trouble in the middle of the interval and must be split before anything else.`,
        hint: 'Ask which end the trouble is at. The two p-tests point in opposite directions.',
      });
    },
  }),

  template({
    id: 'improper-across-asymptote',
    conceptId: 'improper-integrals',
    description: 'Recognise an integral that cannot be evaluated by the fundamental theorem.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(1, 8);
      const k = rng.pick([1, 3, 5]);

      return choiceExercise({
        prompt: 'What is the value of this integral?',
        latex: `\\int_{-${a}}^{${a}} \\frac{dx}{${k === 1 ? 'x' : `x^{${k}}`}}`,
        ...options(
          [
            { label: 'It diverges — the integral has no value', correct: true },
            { label: '0, by symmetry', misconceptionId: 'improper-interior-discontinuity-ignored' },
            { label: 'The bracket evaluates to zero, so the answer is zero', misconceptionId: 'improper-limit-not-taken' },
            { label: 'Infinity', misconceptionId: 'improper-p-test-reversed' },
          ],
          rng.shuffle,
        ),
        summary: `The integrand blows up at x = 0, which sits inside the interval, so the fundamental theorem does not apply and the integral has to be split there. Each half diverges, so the whole does. The two infinities do not cancel — the bracket evaluation cancels them without permission.`,
        hint: 'Check the integrand for a discontinuity inside the interval before writing any antiderivative down.',
      });
    },
  }),
];
